import React, { useState } from 'react';
import { Button, Table, Form, Alert, Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import {
  useCreateHouseMutation,
  useUploadHouseImagesMutation,
} from '../../slices/housesApiSlice';

const BulkExcelCreateHouseScreen = () => {
  const [rows, setRows] = useState([]);
  const [results, setResults] = useState([]);
  const [fileName, setFileName] = useState('');
  const [createHouse, { isLoading: loadingCreate }] = useCreateHouseMutation();
  const [uploadHouseImages, { isLoading: loadingUpload }] = useUploadHouseImagesMutation();

  const validateRow = (row) => {
    if (!row.name) return 'Missing Name';
    if (!row.type) return 'Missing Type';
    if (!row.address) return 'Missing Address';
    if (isNaN(parseFloat(row.regularPrice))) return 'Invalid Regular Price';
    return null;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const parsed = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      const enriched = parsed.map((row, index) => ({
        rowIndex: index + 2,
        type: row.Type,
        name: row.Name || 'Sample House',
        bedrooms: parseInt(row.Bedrooms || 0),
        bathrooms: parseInt(row.Bathrooms || 0),
        parking: row.Parking === 'Yes',
        furnished: row.Furnished === 'Yes',
        address: row.Address || 'Sample Address',
        offer: row.Offer === 'Yes',
        regularPrice: parseFloat(row.RegularPrice || 0),
        discountedPrice: parseFloat(row.DiscountedPrice || 0),
        latitude: parseFloat(row.Latitude || 0),
        longitude: parseFloat(row.Longitude || 0),
        size: row.Size || '',
        availability: row.Availability !== 'No',
        description: row.Description || 'Sample description',
        amenities: row.Amenities ? row.Amenities.split(',').map((a) => a.trim()) : [],
        agent: row.Agent || 'Sample Agent',
        images: row.Images
          ? row.Images.split(',').map((url) => url.trim()).slice(0, 10)
          : ['/images/sample1.jpg'],
        error: null,
      }));

      enriched.forEach((row) => {
        row.error = validateRow(row);
      });

      setRows(enriched);
      setResults([]);
      toast.success(`Loaded ${enriched.length} rows from ${file.name}`);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFieldChange = (index, field, value) => {
    const updated = [...rows];
    updated[index][field] = field === 'regularPrice' || field === 'discountedPrice'
      ? parseFloat(value)
      : value;
    updated[index].error = validateRow(updated[index]);
    setRows(updated);
  };

  const handleImageUpload = async (e, index) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const formData = new FormData();
    files.forEach((file) => formData.append('images', file));

    try {
      const res = await uploadHouseImages(formData).unwrap();
      const updated = [...rows];
      const currentImages = updated[index].images || [];

      updated[index].images = [...currentImages, ...res.images].slice(0, 10);
      setRows(updated);
      toast.success('Images uploaded');
    } catch (err) {
      toast.error(err?.data?.message || err.message);
    }
  };

  const handleImageDelete = (rowIndex, imageIndex) => {
    const updated = [...rows];
    updated[rowIndex].images = updated[rowIndex].images.filter((_, i) => i !== imageIndex);
    setRows(updated);
  };

  const handleSubmit = async () => {
    const feedback = [];

    for (const row of rows) {
      if (row.error) {
        feedback.push({ index: row.rowIndex, status: 'Skipped', message: row.error });
        continue;
      }

      const { rowIndex, error, ...validPayload } = row;

      try {
        await createHouse(validPayload).unwrap();
        feedback.push({ index: rowIndex, status: 'Success', message: `Created ${validPayload.name}` });
      } catch (err) {
        feedback.push({
          index: rowIndex,
          status: 'Failed',
          message: err?.data?.message || err.message,
        });
      }

      await new Promise((res) => setTimeout(res, 200));
    }

    setResults(feedback);
    toast.success('House creation process completed');
  };

  return (
    <div className="container mt-4">
      <h2>🏗️ Bulk Create Houses from Excel</h2>

      <Form.Group controlId="excelUpload" className="mb-3">
        <Form.Label>Upload Excel File (.xlsx)</Form.Label>
        <Form.Control type="file" accept=".xlsx" onChange={handleFileUpload} />
      </Form.Group>

      {rows.length > 0 && (
        <>
          <h5>Preview: {fileName}</h5>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Row</th>
                <th>Name</th>
                <th>Type</th>
                <th>Address</th>
                <th>Agent</th>
                <th>Price</th>
                <th>Discount</th>
                <th>Size</th>
                <th>Images</th>
                <th>Upload</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} style={{ background: row.error ? '#fff3cd' : 'inherit' }}>
                  <td>{row.rowIndex}</td>
                  <td>
                    <Form.Control
                      type="text"
                      value={row.name}
                      onChange={(e) => handleFieldChange(idx, 'name', e.target.value)}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      value={row.type}
                      onChange={(e) => handleFieldChange(idx, 'type', e.target.value)}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      value={row.address}
                      onChange={(e) => handleFieldChange(idx, 'address', e.target.value)}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      value={row.agent}
                      onChange={(e) => handleFieldChange(idx, 'agent', e.target.value)}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      value={row.regularPrice}
                      onChange={(e) => handleFieldChange(idx, 'regularPrice', e.target.value)}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      value={row.discountedPrice}
                      onChange={(e) => handleFieldChange(idx, 'discountedPrice', e.target.value)}
                    />
                  </td>
                  <td>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={row.size}
                  onChange={(e) => handleFieldChange(idx, 'size', e.target.value)}
                />
              </td>
                  <td>
                    {row.images.map((img, i) => (
                      <div key={i} style={{ display: 'inline-block', position: 'relative', marginRight: '5px' }}>
                        <img
                          src={img}
                          alt="preview"
                          style={{ width: '40px', height: '40px', cursor: 'pointer', borderRadius: '4px' }}
                          onClick={() => window.open(img, '_blank')}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          style={{
                            position: 'absolute',
                            top: '-5px',
                            right: '-5px',
                            padding: '0 5px',
                            fontSize: '0.7rem',
                            lineHeight: '1',
                          }}
                          onClick={() => handleImageDelete(idx, i)}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                    <div style={{ fontSize: '0.8rem' }}>{row.images.length} / 10</div>
                  </td>
                  <td>
                    <Form.Control
                      type="file"
                      multiple
                      onChange={(e) => handleImageUpload(e, idx)}
                      disabled={row.images.length >= 10}
                    />
                    {loadingUpload && <Spinner animation="border" size="sm" />}
                  </td>
                                    <td>
                    {row.error
                      ? <span className="text-danger">{row.error}</span>
                      : <span className="text-success">✅ Ready</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Button onClick={handleSubmit} disabled={loadingCreate}>
            {loadingCreate ? <Spinner animation="border" size="sm" /> : 'Create Houses'}
          </Button>
        </>
      )}

      {results.length > 0 && (
        <div className="mt-4">
          <h5>📋 Creation Results</h5>
          {results.map((r, i) => (
            <Alert key={i} variant={
              r.status === 'Success' ? 'success' :
              r.status === 'Skipped' ? 'warning' :
              'danger'
            }>
              Row {r.index}: {r.status} — {r.message}
            </Alert>
          ))}
        </div>
      )}
    </div>
  );
};

export default BulkExcelCreateHouseScreen;